/**
 * Undo/redo bookkeeping, extracted from the store so all three tools share one
 * implementation — and so it can be unit-tested without zustand or a DOM.
 *
 * The snapshots are opaque (`unknown`): a slice never inspects what it holds,
 * and the type safety lives in the store's typed `commit()` wrapper instead.
 */

export const HISTORY_LIMIT = 80;
/** Rapid edits sharing a merge key inside this window collapse into one entry. */
export const MERGE_WINDOW = 500;

export interface HistorySlice {
  past: unknown[];
  future: unknown[];
  mergeKey: string | null;
  mergeAt: number;
}

export function emptyHistory(): HistorySlice {
  return { past: [], future: [], mergeKey: null, mergeAt: 0 };
}

export function shouldCoalesce(
  h: HistorySlice,
  mergeKey: string | undefined,
  now: number,
): boolean {
  return !!mergeKey && h.mergeKey === mergeKey && now - h.mergeAt < MERGE_WINDOW;
}

/** Mutates the slice — it is always called on an immer draft. */
export function pushSnapshot(h: HistorySlice, prev: unknown, now: number): void {
  h.past.push(prev);
  if (h.past.length > HISTORY_LIMIT) h.past.shift();
  h.future = [];
  h.mergeAt = now;
}

/** Returns the document to restore, having stashed `current` for redo. */
export function takeUndo(h: HistorySlice, current: unknown): unknown | undefined {
  const prev = h.past.pop();
  if (prev === undefined) return undefined;
  h.future.unshift(current);
  h.mergeKey = null;
  return prev;
}

export function takeRedo(h: HistorySlice, current: unknown): unknown | undefined {
  const next = h.future.shift();
  if (next === undefined) return undefined;
  h.past.push(current);
  h.mergeKey = null;
  return next;
}

export function resetHistory(h: HistorySlice): void {
  h.past = [];
  h.future = [];
  h.mergeKey = null;
  h.mergeAt = 0;
}
